import { Injectable } from '@angular/core';
import { API } from '@core/services/api.base';
import { Observable } from 'rxjs';
import { AppointmentEntity } from 'src/app/models/appointment.model';
import { CartItem } from 'src/app/store/cart/cart.models';

@Injectable({
  providedIn: 'root',
})
export class AppointmentService extends API {
  getAppointmentById(appointmentId: string): Observable<AppointmentEntity> {
    return this.get<AppointmentEntity>(`v1/appointment/${appointmentId}`);
  }

  checkInAppointment(
    storeId: string,
    phoneNumber: string
  ): Observable<{ appointment: AppointmentEntity; pointsAdded: number }> {
    return this.post<
      { appointment: AppointmentEntity; pointsAdded: number },
      { storeId: string; phoneNumber: string }
    >('v1/appointment/check-in', { storeId, phoneNumber });
  }

  requestAppointmentFromCheckIn(
    storeId: string,
    phoneNumber: string,
    userId: string
  ): Observable<{ notificationId: string }> {
    return this.post<
      { notificationId: string },
      { storeId: string; phoneNumber: string; userId: string }
    >('v1/appointment/request-appointment-from-check-in-guest', {
      storeId,
      phoneNumber,
      userId,
    });
  }

  getUserAppointments(): Observable<AppointmentEntity[]> {
    return this.get<AppointmentEntity[]>(`v1/appointment/user`);
  }

  createAppointment(
    appointmentData: CreateAppointmentRequest
  ): Observable<AppointmentEntity> {
    return this.post<AppointmentEntity, CreateAppointmentRequest>(
      'v1/appointment',
      appointmentData
    );
  }

  confirmAppointment(appointmentId: string): Observable<null> {
    return this.post<null, void>(
      `v1/appointment/${appointmentId}/confirm`,
      undefined
    );
  }

  cancelAppointment(appointmentId: string, reason?: string): Observable<null> {
    return this.put<null, { reason?: string }>(
      `v1/appointment/${appointmentId}/cancel`,
      { reason }
    );
  }
  getUpcomingAppointmentsForCustomer(): Observable<AppointmentEntity[]> {
    return this.get<AppointmentEntity[]>(`v1/appointment/upcoming/customer`);
  }

  /**
   * Check staff availability for a specific datetime
   * @param staffIds Comma-separated list of staff IDs
   * @param datetime ISO string of the datetime to check
   * @returns Map of staffId to array of available time slots
   */
  getStaffAvailability(
    staffIds: string[],
    datetime: string,
    storeId: string
  ): Observable<Record<string, Date[]>> {
    const staffIdsParam = staffIds.join(',');
    return this.get<Record<string, Date[]>>(
      `v1/appointment/staffs-availability?staffIds=${staffIdsParam}&datetime=${encodeURIComponent(datetime)}&storeId=${storeId}`
    );
  }
}

export interface CreateAppointmentRequest {
  userInfo: {
    displayName: string;
    phoneNumber: string;
    photoURL: string;
    id: string;
  };
  date: string; // ISO string format
  duration: number;
  storeId: string;
  services: Record<string, ServiceData>;
  staffs: StaffInfo[];
  isCheckedIn: boolean;
  isBookedOnline: boolean;
}

export interface ServiceData {
  combos: ServiceCombo[];
  items: ServiceItem[];
  duration: number;
  start: string; // ISO string format
}

export interface ServiceCombo {
  id: string;
  name: string;
  duration: number;
  totalPrice: number;
  deductionAmount: number;
  discountAmount: number;
  taxAmount: number;
}

export interface ServiceItem {
  id: string;
  name: string;
  duration: number;
  totalPrice: number;
  deductionAmount: number;
  discountAmount: number;
  taxAmount: number;
}

export interface StaffInfo {
  id: string;
  displayName: string;
  photoURL: string;
}
